import { useTableContext } from '@edge/spark/io-react-table'
import { isMac } from '@edge/spark/utils'
import { Box, Button, Stack, Tooltip } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'

type PureProps = {
    isLastPage: boolean
    formIsSubmitting: boolean
    disabled: boolean
}

type ContextProps = {
    disabled?: boolean
}

const saveAndDoneTooltip = `Save & Done — ${isMac ? 'Option [⌥]' : 'Alt'} + D`
const saveAndNextTooltip = `Save & Next — ${isMac ? 'Option [⌥]' : 'Alt'} + N`

export const ActionDrawerSubmitButtonsPure = ({ isLastPage, formIsSubmitting, disabled }: PureProps) => {
    const saveAndCloseRef = React.useRef<HTMLButtonElement>(null)
    const saveAndDoneRef = React.useRef<HTMLButtonElement>(null)
    const saveAndNextRef = React.useRef<HTMLButtonElement>(null)

    const handleKeyPress = React.useCallback((event: KeyboardEvent) => {
        /**
         * Option/Alt + D Keyboard Shortcut
         * ⌥ + D (macOS) or ⎇ + D (other OS)
         * Used for "Save & Done" button press
         *
         * On macOS, ⌥ + D is typically mapped to the ∂ (\u2202 PARTIAL DIFFERENTIAL) character
         */
        if (
            event.altKey &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.shiftKey &&
            (event.key === 'd' || (isMac && event.key === '∂'))
        ) {
            event.preventDefault()
            saveAndDoneRef.current?.click()
            saveAndCloseRef.current?.click()
        }
        /**
         * Option/Alt + N Keyboard Shortcut
         * ⌥ + N (macOS) or ⎇ + N (other OS)
         * Used for "Save & Next" button press
         *
         * On macOS, ⌥ + N is typically mapped to the ˜ (\u02DC SMALL TILDE) character,
         * which creates an `event.key` of 'Dead', so we need to use `event.code === 'KeyN'` or
         * the deprecated `event.keyCode === 78` instead. However, `code` may not work for all
         * keyboard layouts (e.g. French, German, Dvorak, etc...).
         *
         * Reference: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
         * Reference: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
         * Reference: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
         * Reference: https://en.wikipedia.org/wiki/Dead_key
         * Keycode Tester: https://keyjs.dev/
         */
        if (
            event.altKey &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.shiftKey &&
            (event.key === 'n' || (isMac && event.key === 'Dead' && event.keyCode === 78))
        ) {
            event.preventDefault()
            saveAndNextRef.current?.click()
        }
    }, [])

    /** Listen for all keyboard events */
    React.useEffect(() => {
        document.addEventListener('keydown', handleKeyPress)

        return () => {
            document.removeEventListener('keydown', handleKeyPress)
        }
    }, [handleKeyPress])

    if (isLastPage) {
        return (
            <Box flex={0} className="foo">
                <Tooltip title={saveAndDoneTooltip} placement="top">
                    <Box flex={1}>
                        <Button
                            ref={saveAndDoneRef}
                            type="submit"
                            name="save-and-done"
                            fullWidth
                            variant="contained"
                            disabled={disabled || formIsSubmitting}
                        >
                            Save & Done
                        </Button>
                    </Box>
                </Tooltip>
            </Box>
        )
    }

    return (
        <Stack gap={2} direction="row">
            <Tooltip title={saveAndDoneTooltip} placement="top">
                <Box flex={1}>
                    <Button
                        ref={saveAndCloseRef}
                        type="submit"
                        name="save-and-done"
                        fullWidth
                        variant="outlined"
                        disabled={disabled || formIsSubmitting}
                    >
                        Save & Done
                    </Button>
                </Box>
            </Tooltip>
            <Tooltip title={saveAndNextTooltip} placement="top">
                <Box flex={1}>
                    <Button
                        ref={saveAndNextRef}
                        type="submit"
                        name="save-and-next"
                        fullWidth
                        variant="contained"
                        disabled={disabled || formIsSubmitting}
                    >
                        Save & Next
                    </Button>
                </Box>
            </Tooltip>
        </Stack>
    )
}

export const ActionDrawerSubmitButtons = ({ disabled }: ContextProps) => {
    const isLastPage = useTableContext((s) => s.getIsLastPage())
    const formIsSubmitting = useFormContext()?.formState.isSubmitting
    return (
        <ActionDrawerSubmitButtonsPure
            isLastPage={isLastPage}
            formIsSubmitting={formIsSubmitting}
            disabled={disabled || false}
        />
    )
}
